import { useParams, useNavigate } from 'react-router-dom'
import { useForm, useFieldArray } from 'react-hook-form'
import { FileText, Plus, Trash2, XCircle } from 'lucide-react'
import { PageShell } from '@/components/common/PageShell'
import { WorkspaceToolbar } from '@/components/common/WorkspaceToolbar'
import { TableSkeleton } from '@/components/common/TableSkeleton'
import { EmptyState } from '@/components/common/EmptyState'
import { Button } from '@/components/ui/Button'
import { Select } from '@/components/ui/Select'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import {
  usePayrollRun,
  usePayrollRunItems,
  useCreateAdjustment,
} from '@/features/payroll/hooks/usePayrollRuns'
import { StatusBadge } from './StatusBadge'
import { StatCard } from './StatCard'
import type { PayrollItem } from '@/features/payroll/types/payroll.types'
import { userSubline } from '@/lib/formatters'
import { useState } from 'react'

const SOURCE_TYPE_OPTIONS = [
  { value: 'compensation', label: 'Compensation' },
  { value: 'bonus', label: 'Bonus' },
  { value: 'penalty', label: 'Penalty' },
  { value: 'allowance', label: 'Allowance' },
  { value: 'overtime', label: 'Overtime' },
  { value: 'leave_deduction', label: 'Leave Deduction' },
  { value: 'security_ledger', label: 'Security Ledger' },
  { value: 'attendance', label: 'Attendance' },
]

interface AdjLineForm {
  employeeId: string
  sourceType: string
  description: string
  amount: number
  currency: string
}

interface FormValues {
  reason: string
  lines: AdjLineForm[]
}

const inputCls =
  'h-8 w-full rounded-md border border-gray-300 bg-white px-2.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100'

function getEmployeeName(emp: PayrollItem['employeeId']): string {
  if (typeof emp === 'string') return emp
  return [emp.firstName, emp.lastName].filter(Boolean).join(' ') || emp._id
}

export function AdjustmentFormPage(): React.ReactElement {
  const { companyId, runId } = useParams<{ companyId: string; runId: string }>()
  const navigate = useNavigate()
  const [confirmOpen, setConfirmOpen] = useState(false)

  const { data: run, isLoading: runLoading } = usePayrollRun(companyId ?? null, runId ?? null)
  const { data: itemsData, isLoading: itemsLoading } = usePayrollRunItems(
    companyId ?? null,
    runId ?? null,
    1,
    100,
  )
  const createAdj = useCreateAdjustment()

  const form = useForm<FormValues>({
    defaultValues: {
      reason: '',
      lines: [{ employeeId: '', sourceType: 'compensation', description: '', amount: 0, currency: '' }],
    },
  })
  const linesField = useFieldArray({ control: form.control, name: 'lines' })
  const watchedLines = form.watch('lines')
  const watchedReason = form.watch('reason')

  if (!companyId || !runId) {
    navigate('/companies', { replace: true })
    return <></>
  }

  const isLoading = runLoading || itemsLoading

  if (isLoading) {
    return (
      <PageShell
        breadcrumbItems={[
          { label: 'Payroll Runs', to: `/companies/${companyId}/payroll/runs` },
          { label: 'Adjustment' },
        ]}
        icon={FileText}
        title="Post-Approval Adjustment"
        className="gap-0"
        hideHeader
      >
        <TableSkeleton rows={4} cols={3} />
      </PageShell>
    )
  }

  if (!run) {
    return (
      <PageShell
        breadcrumbItems={[
          { label: 'Payroll Runs', to: `/companies/${companyId}/payroll/runs` },
          { label: 'Not Found' },
        ]}
        icon={FileText}
        title="Post-Approval Adjustment"
        className="gap-0"
        hideHeader
      >
        <EmptyState icon={XCircle} title="Payroll run not found" />
      </PageShell>
    )
  }

  if (run.status !== 'approved') {
    return (
      <PageShell
        breadcrumbItems={[
          { label: 'Payroll Runs', to: `/companies/${companyId}/payroll/runs` },
          { label: 'Adjustment' },
        ]}
        icon={FileText}
        title="Post-Approval Adjustment"
        className="gap-0"
        hideHeader
      >
        <EmptyState
          icon={XCircle}
          title="Adjustments can only be created for approved runs"
          description={`This run is currently "${run.status}".`}
        />
      </PageShell>
    )
  }

  const period = `${run.payrollMonth.year}-${String(run.payrollMonth.month).padStart(2, '0')}`
  const items = itemsData?.data ?? []
  const employeeOptions = items.map((item: PayrollItem) => ({
    value: typeof item.employeeId === 'string' ? item.employeeId : item.employeeId._id,
    label: getEmployeeName(item.employeeId),
    description:
      typeof item.employeeId === 'string'
        ? undefined
        : userSubline(undefined, item.employeeId.email),
  }))

  // Live calculations
  const totalPositive = watchedLines.reduce((s, l) => s + Math.max(0, Number(l.amount) || 0), 0)
  const totalNegative = watchedLines.reduce((s, l) => s + Math.min(0, Number(l.amount) || 0), 0)
  const netEffect = totalPositive + totalNegative

  const canSubmit =
    watchedReason.trim().length >= 10 &&
    watchedLines.length > 0 &&
    watchedLines.every((l) => l.employeeId && l.description && l.amount !== 0)

  const handleSubmit = (): void => {
    const values = form.getValues()
    createAdj.mutate(
      {
        companyId,
        runId,
        body: {
          reason: values.reason,
          lines: values.lines.map((l) => ({
            employeeId: l.employeeId,
            sourceType: l.sourceType,
            description: l.description,
            amount: Number(l.amount),
            currency: l.currency || undefined,
          })),
        },
      },
      {
        onSuccess: (adjRun) => {
          setConfirmOpen(false)
          navigate(`/companies/${companyId}/payroll/runs/${adjRun._id}`)
        },
      },
    )
  }

  return (
    <PageShell
      breadcrumbItems={[
        { label: 'Payroll Runs', to: `/companies/${companyId}/payroll/runs` },
        { label: `Run ${period}`, to: `/companies/${companyId}/payroll/runs/${runId}` },
        { label: 'Adjustment' },
      ]}
      icon={FileText}
      title={`Adjustment — ${period}`}
      description="Create a correction run for this approved payroll"
      className="gap-0"
      hideHeader
    >
      <div className="space-y-4">
        <WorkspaceToolbar actions={<StatusBadge status={run.status} />} />
        <div className="max-w-3xl space-y-6">
        {/* Info banner */}
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700 dark:border-blue-900/50 dark:bg-blue-900/20 dark:text-blue-300">
          This will create a new correction run in "calculated" status. It does not modify the
          original run.
        </div>

        {/* Reason */}
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Reason <span className="text-red-500">*</span>
          </label>
          <textarea
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
            rows={3}
            placeholder="Explain why this adjustment is needed (min 10 characters)..."
            {...form.register('reason')}
          />
          {watchedReason.length > 0 && watchedReason.trim().length < 10 && (
            <p className="mt-1 text-xs text-red-600">
              Minimum 10 characters ({watchedReason.trim().length}/10)
            </p>
          )}
        </div>

        {/* Lines */}
        <div>
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Adjustment Lines
            </h3>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() =>
                linesField.append({
                  employeeId: '',
                  sourceType: 'compensation',
                  description: '',
                  amount: 0,
                  currency: '',
                })
              }
            >
              <Plus className="mr-1 h-3 w-3" />
              Add Line
            </Button>
          </div>

          <div className="space-y-3">
            {linesField.fields.map((field, i) => (
              <div
                key={field.id}
                className="rounded-lg border border-gray-200 p-3 dark:border-gray-700"
              >
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-500">Line {i + 1}</span>
                  {linesField.fields.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => linesField.remove(i)}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-red-500" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Select
                    label="Employee"
                    options={employeeOptions}
                    value={watchedLines[i]?.employeeId ?? ''}
                    onChange={(v) => form.setValue(`lines.${i}.employeeId`, v)}
                    searchable
                  />
                  <Select
                    label="Source Type"
                    options={SOURCE_TYPE_OPTIONS}
                    value={watchedLines[i]?.sourceType ?? 'compensation'}
                    onChange={(v) => form.setValue(`lines.${i}.sourceType`, v)}
                  />
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-500">
                      Description
                    </label>
                    <input
                      className={inputCls}
                      placeholder="e.g. Overtime correction"
                      {...form.register(`lines.${i}.description`)}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-500">
                      Amount (negative for deductions)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      className={inputCls}
                      {...form.register(`lines.${i}.amount`, { valueAsNumber: true })}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Live summary */}
        <div className="grid grid-cols-3 gap-4">
          <StatCard label="Additions" value={totalPositive} currency={run.currency} />
          <StatCard
            label="Deductions"
            value={totalNegative}
            currency={run.currency}
            className={totalNegative < 0 ? 'border-red-200 dark:border-red-900/50' : ''}
          />
          <StatCard
            label="Net Effect"
            value={netEffect}
            currency={run.currency}
            className={
              netEffect > 0
                ? 'border-green-200 dark:border-green-900/50'
                : netEffect < 0
                  ? 'border-red-200 dark:border-red-900/50'
                  : ''
            }
          />
        </div>

        {/* Submit */}
        <div className="flex gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
          <Button
            variant="secondary"
            onClick={() => navigate(`/companies/${companyId}/payroll/runs/${runId}`)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!canSubmit} loading={createAdj.isPending}
            onClick={() => setConfirmOpen(true)}
          >
            Create Adjustment Run
          </Button>
        </div>
        </div>

        <ConfirmDialog
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          onConfirm={handleSubmit}
          title="Create Adjustment Run"
          message={`This will create a correction run with ${watchedLines.length} line(s) and a net effect of ${netEffect.toLocaleString(undefined, { minimumFractionDigits: 2 })} ${run.currency}.`}
          confirmLabel="Create"
          confirmLoading={createAdj.isPending}
        />
      </div>
    </PageShell>
  )
}

AdjustmentFormPage.displayName = 'AdjustmentFormPage'
